import { useState, useRef, useEffect } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { GlassCard } from "../components/GlassCard";
import { UploadCloud, FileText, FileSpreadsheet, Search, X } from "lucide-react";

const fmtNum = (n) => {
  if (n == null || Number.isNaN(n)) return "?";
  return Math.round(Number(n)).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export default function Backfill() {
  const [tab, setTab] = useState("pdf");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [clearedInfo, setClearedInfo] = useState(null);
  const [reviewedWarnings, setReviewedWarnings] = useState(false);
  const [err, setErr] = useState("");
  const [progressLog, setProgressLog] = useState([]);
  const [progressPct, setProgressPct] = useState(0);
  const [inspectData, setInspectData] = useState(null);      // modal payload
  const [inspectLoading, setInspectLoading] = useState(false);
  const [inspectTypeChoice, setInspectTypeChoice] = useState("auto"); // auto | ledger | balance_sheet
  const [previewToken, setPreviewToken] = useState(null);    // set after a successful dry-run
  const [previewFileName, setPreviewFileName] = useState(null); // to invalidate token if user swaps file
  const [savedSources, setSavedSources] = useState([]);      // GET /backfill/uploads list
  const [selectedSourceId, setSelectedSourceId] = useState(null);   // reuse a saved source
  const [savingSource, setSavingSource] = useState(false);
  const logEndRef = useRef(null);

  const refreshSavedSources = async () => {
    try {
      const { data } = await api.get("/backfill/uploads");
      setSavedSources(data.uploads || data.items || data || []);
    } catch (_e) {
      // silent — endpoint may 404 on very old backends
    }
  };
  useEffect(() => { refreshSavedSources(); }, []);

  const saveSourceOnly = async () => {
    if (!file) return;
    setSavingSource(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backfill/save-source", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pushLog(`✓ Saved '${data.filename}' on server (${(data.size / 1024 / 1024).toFixed(1)} MB) — will show below.`);
      await refreshSavedSources();
      setSelectedSourceId(data.upload_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(detail ? formatApiErrorDetail(detail) : e.message);
    } finally {
      setSavingSource(false);
    }
  };

  const deleteSource = async (id) => {
    if (!confirm("Delete this saved source? Entries already imported from it are unaffected.")) return;
    try {
      await api.delete(`/backfill/uploads/${id}`);
      await refreshSavedSources();
      if (selectedSourceId === id) setSelectedSourceId(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [progressLog]);

  const pushLog = (msg) => setProgressLog((l) => [...l, { t: new Date().toLocaleTimeString(), msg }]);

  const uploadStreaming = async (dryRun = false) => {
    // Two entry paths: use a saved source (no upload) OR upload a fresh file.
    const useSaved = !!selectedSourceId;
    if (!useSaved && !file) return;
    setBusy(true);
    setErr("");
    setResult(null);
    setProgressLog([]);
    setProgressPct(0);

    // FAST PATH: if Import + we have a fresh preview_token for the same file, commit directly.
    if (!dryRun && previewToken && !useSaved && file.name === previewFileName) {
      return commitFromPreview();
    }

    try {
      const token = localStorage.getItem("access_token");
      const ws = localStorage.getItem("workspace_id");
      const mode = localStorage.getItem("app_mode") || "live";
      const qs = new URLSearchParams();
      qs.set("stream", "true");
      if (dryRun) qs.set("dry_run", "true");
      if (useSaved) qs.set("source_upload_id", selectedSourceId);
      const url = `${API}/backfill/pdf?${qs.toString()}`;

      let body;
      if (useSaved) {
        // No file body — server reads bytes from stored source.
        body = null;
        pushLog(`⚡ Reusing saved source (${selectedSourceId}) — no upload needed`);
      } else {
        const fd = new FormData();
        fd.append("file", file);
        body = fd;
        pushLog(`Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)…`);
      }

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(ws ? { "X-Workspace": ws } : {}),
          "X-Mode": mode,
        },
        body: body,
      });

      if (!resp.ok) {
        const text = await resp.text();
        let detail = null;
        try { detail = JSON.parse(text).detail; } catch { /* not json */ }
        const msg = detail
          ? formatApiErrorDetail(detail)
          : `HTTP ${resp.status} · ${text.slice(0, 300)}`;
        setErr(msg);
        pushLog(`❌ ${msg}`);
        setBusy(false);
        return;
      }

      // Stream NDJSON: one JSON object per line
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalResult = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nlIdx;
        while ((nlIdx = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, nlIdx).trim();
          buffer = buffer.slice(nlIdx + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { pushLog(`(unparseable event: ${line.slice(0, 60)})`); continue; }

          switch (evt.event) {
            case "upload_persist_start":
              pushLog(`Storing upload (${(evt.size / 1024 / 1024).toFixed(1)} MB) for later download…`);
              break;
            case "upload_persist_done":
              pushLog("✓ Upload stored");
              break;
            case "start":
              pushLog(`Found ${evt.total_files} PDF${evt.total_files > 1 ? "s" : ""} to process${evt.has_balance_sheet ? " + balance sheet" : ""}${evt.dry_run ? " (dry run)" : ""}`);
              break;
            case "file_start":
              pushLog(`[${evt.index}/${evt.total}] Parsing ${evt.source}…`);
              setProgressPct(Math.round(((evt.index - 1) / evt.total) * 100));
              break;
            case "file_parsed":
              pushLog(`  → account "${evt.account_name}", ${evt.entries_count} rows${evt.warnings_count ? `, ${evt.warnings_count} warning${evt.warnings_count > 1 ? "s" : ""}` : ""}`);
              break;
            case "balance_sheet_loaded":
              pushLog(`✓ Balance sheet loaded from ${evt.source} (${evt.accounts_count} accounts) — will spot-check each file's closing balance against it`);
              break;
            case "balance_sheet_error":
              pushLog(`⚠ Could not parse balance sheet ${evt.source}: ${evt.error} — per-file checks disabled`);
              break;
            case "file_balance_ok":
              pushLog(`  ✓ Closing balance ${fmtNum(evt.computed_close)} matches balance sheet${Math.abs(evt.diff) > 0 ? ` (diff ${fmtNum(evt.diff)})` : ""}`);
              break;
            case "file_balance_missing":
              pushLog(`  · Closing balance ${fmtNum(evt.computed_close)} — account not on balance sheet (likely zero; safe to ignore)`);
              break;
            case "file_balance_mismatch":
              pushLog(`  ⚠ MISMATCH: this file closes at ${fmtNum(evt.computed_close)} but balance sheet expects ${fmtNum(evt.expected_close)} (off by ${fmtNum(evt.diff)}) — stop and Inspect this file`);
              break;
            case "aborted_on_mismatch":
              pushLog(`⛔ Aborted dry-run at ${evt.source} — opening Inspect view so you can see the parsed rows`);
              setInspectData(evt.inspect);
              setBusy(false);
              return;
            case "file_done":
              pushLog(`  ✓ Done: +${evt.result.created_entries} entries, +${evt.result.created_accounts} accounts, ${evt.result.duplicate_entries} dupes skipped`);
              setProgressPct(Math.round((evt.index / (evt.result.__total || 1)) * 100));
              break;
            case "file_error":
              pushLog(`  ❌ Error in ${evt.source}: ${evt.error}`);
              break;
            case "tally_start":
              pushLog(`Tallying against ${evt.source}…`);
              break;
            case "tally_done":
              if (evt.tally.all_tallied) pushLog(`✓ Balance sheet tally: all ${evt.tally.matches} accounts match`);
              else pushLog(`⚠ Tally: ${evt.tally.matches} match, ${evt.tally.mismatches.length} mismatch, ${evt.tally.unknown_accounts.length} unknown`);
              break;
            case "post_import_verify":
              if (evt.all_tallied) {
                pushLog(`✓ Post-import verification (DB): all ${evt.matches} accounts match the uploaded balance sheet — you're done.`);
              } else {
                pushLog(`⚠ Post-import verification (DB): ${evt.matches} match · ${evt.mismatches_count} mismatch · ${evt.unknown_count} unknown — the app's Balance Sheet will differ from the source PDF, investigate below:`);
                (evt.tally.mismatches || []).slice(0, 20).forEach((m) => {
                  pushLog(`      ${m.name}: expected ${fmtNum(m.expected)} vs actual ${fmtNum(m.actual)} (diff ${fmtNum(m.diff)})`);
                });
              }
              break;
            case "post_import_verify_error":
              pushLog(`⚠ Post-import verify failed: ${evt.error}`);
              break;
            case "tally_error":
              pushLog(`⚠ Tally error: ${evt.error}`);
              break;
            case "complete":
              finalResult = evt;
              setProgressPct(100);
              pushLog(`✓ Complete — ${evt.files_processed} file(s) processed`);
              if (evt.preview_token) {
                setPreviewToken(evt.preview_token);
                setPreviewFileName(file ? file.name : `saved:${selectedSourceId || ""}`);
                pushLog(`💾 Preview cached — clicking "Import now" will commit directly without re-uploading.`);
              } else if (evt.committed_from_preview) {
                setPreviewToken(null);
                setPreviewFileName(null);
              }
              break;
            default:
              pushLog(`(unknown event ${evt.event})`);
          }
        }
      }

      if (finalResult) {
        setResult({ ...finalResult, _isPreview: dryRun });
        setReviewedWarnings(false);
      }
      // Refresh library so fresh uploads (auto-persisted by backend) show up
      refreshSavedSources();
    } catch (e) {
      const msg = e.message || "Upload failed";
      setErr(msg);
      pushLog(`❌ ${msg}`);
      console.error("Backfill upload failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const commitFromPreview = async () => {
    setBusy(true);
    setErr("");
    setResult(null);
    setProgressLog([]);
    setProgressPct(0);
    try {
      const token = localStorage.getItem("access_token");
      const ws = localStorage.getItem("workspace_id");
      const mode = localStorage.getItem("app_mode") || "live";
      pushLog(`⚡ Committing from cached preview (no re-upload)…`);
      const resp = await fetch(`${API}/backfill/commit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(ws ? { "X-Workspace": ws } : {}),
          "X-Mode": mode,
        },
        body: JSON.stringify({ token: previewToken }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        let detail = null;
        try { detail = JSON.parse(text).detail; } catch { /* not json */ }
        const msg = detail ? formatApiErrorDetail(detail) : `HTTP ${resp.status} · ${text.slice(0, 300)}`;
        setErr(msg);
        pushLog(`❌ ${msg}`);
        // Preview cache probably expired — clear the token so next Import
        // falls back to full upload path.
        setPreviewToken(null);
        setPreviewFileName(null);
        setBusy(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalResult = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nlIdx;
        while ((nlIdx = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, nlIdx).trim();
          buffer = buffer.slice(nlIdx + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { pushLog(`(unparseable: ${line.slice(0, 60)})`); continue; }
          switch (evt.event) {
            case "upload_persist_start":
              pushLog(`Storing raw upload (${(evt.size / 1024 / 1024).toFixed(1)} MB) for later download…`);
              break;
            case "upload_persist_done":
              pushLog("✓ Raw upload stored");
              break;
            case "start":
              pushLog(`Writing ${evt.total_files} file(s) to the database…`);
              break;
            case "file_start":
              pushLog(`[${evt.index}/${evt.total}] Writing ${evt.source}…`);
              setProgressPct(Math.round(((evt.index - 1) / evt.total) * 100));
              break;
            case "file_done":
              pushLog(`  ✓ +${evt.result.created_entries} entries, +${evt.result.created_accounts} accounts, ${evt.result.skipped_already_processed || 0} skipped (already processed from other files)`);
              break;
            case "file_error":
              pushLog(`  ❌ ${evt.error}`);
              break;
            case "post_import_verify":
              if (evt.all_tallied) {
                pushLog(`✓ Post-import verification (DB): all ${evt.matches} accounts match the uploaded balance sheet — you're done.`);
              } else {
                pushLog(`⚠ Post-import verification (DB): ${evt.matches} match · ${evt.mismatches_count} mismatch · ${evt.unknown_count} unknown`);
                (evt.tally?.mismatches || []).slice(0, 20).forEach((m) => {
                  pushLog(`      ${m.name}: expected ${fmtNum(m.expected)} vs actual ${fmtNum(m.actual)} (diff ${fmtNum(m.diff)})`);
                });
              }
              break;
            case "post_import_verify_error":
              pushLog(`⚠ Post-import verify failed: ${evt.error}`);
              break;
            case "complete":
              finalResult = evt;
              setProgressPct(100);
              pushLog(`✓ Committed — ${evt.files_processed} file(s) written to DB`);
              setPreviewToken(null);
              setPreviewFileName(null);
              break;
            default:
              pushLog(`(unknown event ${evt.event})`);
          }
        }
      }
      if (finalResult) {
        setResult({ ...finalResult, _isPreview: false });
        setReviewedWarnings(false);
      }
    } catch (e) {
      const msg = e.message || "Commit failed";
      setErr(msg);
      pushLog(`❌ ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (dryRun = false) => {
    if (tab === "pdf") return uploadStreaming(dryRun);
    // Excel path (no streaming yet — small file, single-shot)
    if (!file) return;
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backfill/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ ...data, _isPreview: false });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg = detail ? formatApiErrorDetail(detail) : status ? `HTTP ${status}` : e.message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const inspectFile = async (asType = inspectTypeChoice) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setErr("Inspect requires a single PDF file (not a ZIP). Pick one PDF to preview its raw parse.");
      return;
    }
    setErr("");
    setInspectLoading(true);
    setInspectData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/backfill/inspect?as_type=${asType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInspectData(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(detail ? formatApiErrorDetail(detail) : e.message);
    } finally {
      setInspectLoading(false);
    }
  };

  // Fetch backend build/version on mount so the operator can eyeball whether
  // the EC2 container is running the latest code before starting a 132-file
  // preview that takes minutes.
  const [buildInfo, setBuildInfo] = useState(null);
  useEffect(() => {
    api.get("/backfill/version").then(({ data }) => setBuildInfo(data)).catch(() => setBuildInfo({ error: true }));
  }, []);

  return (
    <div className="space-y-6" data-testid="backfill-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-stone-500">One-time seed</p>
          <h1 className="font-serif text-5xl">Backfill</h1>
        </div>
        {buildInfo && !buildInfo.error && (
          <div
            className="glass rounded-2xl px-4 py-2 text-xs text-stone-600 flex items-center gap-3"
            data-testid="build-info-badge"
            title={JSON.stringify(buildInfo.features, null, 2)}
          >
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Backend build: <span className="font-mono text-emerald-700">{buildInfo.build}</span></span>
          </div>
        )}
        {buildInfo?.error && (
          <div className="glass rounded-2xl px-4 py-2 text-xs text-rose-600" data-testid="build-info-badge-error">
            ⚠ Could not read backend version — restart may be pending
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => { setTab("pdf"); setFile(null); setResult(null); }}
          className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "pdf" ? "bg-[color:var(--gold)] text-white" : "bg-white/70 text-stone-700"}`}
          data-testid="tab-pdf"
        >
          PDF ledger (single or ZIP)
        </button>
        <button
          onClick={() => { setTab("xlsx"); setFile(null); setResult(null); }}
          className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "xlsx" ? "bg-[color:var(--gold)] text-white" : "bg-white/70 text-stone-700"}`}
          data-testid="tab-xlsx"
        >
          Excel (opening balances)
        </button>
      </div>

      <GlassCard>
        <div className="grid md:grid-cols-5 gap-6 items-start">
          <div className="md:col-span-2 space-y-3">
            <div className="w-12 h-12 rounded-2xl glass-gold flex items-center justify-center">
              {tab === "pdf"
                ? <FileText size={22} className="text-[color:var(--gold-deep)]" />
                : <FileSpreadsheet size={22} className="text-[color:var(--gold-deep)]" />}
            </div>
            {tab === "pdf" ? (
              <>
                <h3 className="font-serif text-2xl">PDF ledger backfill</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Upload a single PDF or a ZIP of PDFs (one per account). Each must start with
                  <code className="mx-1 text-xs bg-white/80 px-1.5 py-0.5 rounded">Name : ACCOUNT</code>
                  and list dated rows with counterparty (comment), amount, and balance columns.
                </p>
                <ul className="text-xs text-stone-500 space-y-1 list-disc pl-4">
                  <li>Counterparty accounts are auto-created if new.</li>
                  <li>Rows marked <b>***</b> import as <b>finalized</b> (locked).</li>
                  <li>Duplicate entries across PDFs are skipped.</li>
                </ul>
              </>
            ) : (
              <>
                <h3 className="font-serif text-2xl">Excel opening balances</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  4 columns per row: A=neg name, B=neg amount, C=pos name, D=pos amount. Row 1 is a header.
                </p>
              </>
            )}
          </div>

          <div className="md:col-span-3">
            <label
              htmlFor="backfill-file"
              className="block cursor-pointer glass rounded-2xl p-10 text-center border-2 border-dashed border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]/70 transition-colors"
              data-testid="backfill-dropzone"
            >
              <UploadCloud size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--gold-deep)]" />
              <div className="mt-3 font-serif text-xl">
                {file ? file.name : (tab === "pdf" ? "Choose a PDF or ZIP" : "Choose an Excel (.xlsx)")}
              </div>
              <input
                id="backfill-file"
                type="file"
                accept={tab === "pdf" ? ".pdf,.zip" : ".xlsx,.xls"}
                className="hidden"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  // Any file change invalidates the cached preview.
                  setPreviewToken(null);
                  setPreviewFileName(null);
                  setResult(null);
                }}
                data-testid="backfill-file-input"
              />
            </label>
            {file && (
              tab === "pdf" ? (
                <div className="space-y-2 mt-4">
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => inspectFile("auto")}
                      disabled={busy || inspectLoading || file.name.toLowerCase().endsWith(".zip")}
                      className="btn-ghost border border-[color:var(--gold)]/40 hover:bg-white/80 disabled:opacity-40"
                      title={file.name.toLowerCase().endsWith(".zip") ? "Inspect works on a single PDF, not ZIPs" : "Preview how this PDF is being read (no DB writes)"}
                      data-testid="backfill-inspect-btn"
                    >
                      <Search size={14} className="inline mr-1.5 -mt-0.5" />
                      {inspectLoading ? "…" : "Inspect file"}
                    </button>
                    <button
                      onClick={() => upload(true)}
                      disabled={busy}
                      className="btn-ghost border border-[color:var(--gold)]/40 hover:bg-white/80"
                      data-testid="backfill-preview-btn"
                    >
                      {busy ? "…" : "Preview (dry run)"}
                    </button>
                    <button
                      onClick={() => upload(false)}
                      disabled={busy}
                      className="btn-gold"
                      data-testid="backfill-upload-btn"
                      title={previewToken && file.name === previewFileName ? "Fast path: writes from cached preview, no re-upload" : "Full upload + parse + write"}
                    >
                      {busy ? "Processing…" : (previewToken && file.name === previewFileName ? "⚡ Import from preview" : "Import now")}
                    </button>
                  </div>
                  <button
                    onClick={saveSourceOnly}
                    disabled={busy || savingSource}
                    className="w-full btn-ghost border border-emerald-200 text-emerald-700 hover:bg-emerald-50/70 disabled:opacity-40"
                    title="Store the ZIP/PDF permanently on the server so you can re-run Preview / Import later without re-uploading."
                    data-testid="backfill-save-source-btn"
                  >
                    {savingSource ? "Saving…" : "💾 Save folder on server (no import)"}
                  </button>
                  {!file.name.toLowerCase().endsWith(".zip") && (
                    <p className="text-xs text-stone-500 leading-relaxed">
                      <b>Inspect file</b> shows the raw text pdfplumber extracted + how our parser interpreted it (ledger or balance sheet) — nothing is written to the database. Useful when a specific file has an unusual layout.
                    </p>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => upload(false)}
                  disabled={busy}
                  className="btn-gold w-full mt-4"
                  data-testid="backfill-upload-btn"
                >
                  {busy ? "Processing…" : "Upload & import"}
                </button>
              )
            )}
          </div>
        </div>
      </GlassCard>

      {tab === "pdf" && savedSources.length > 0 && (
        <GlassCard data-testid="backfill-saved-sources-card">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <div>
              <div className="label-tiny text-stone-600">Server library</div>
              <h3 className="font-serif text-2xl">Saved sources ({savedSources.length})</h3>
              <p className="text-xs text-stone-500 mt-1">
                Persisted on the server — survives restarts. Select one below, then click Preview / Import above.
                {selectedSourceId && <span className="ml-2 text-emerald-700">✓ Using saved source (no upload)</span>}
              </p>
            </div>
            {selectedSourceId && (
              <button
                onClick={() => setSelectedSourceId(null)}
                className="btn-ghost text-xs border border-stone-200 hover:bg-white/80"
                data-testid="backfill-clear-selected-source"
              >
                Clear selection
              </button>
            )}
          </div>
          <div className="divide-y divide-stone-100 border border-stone-100 rounded-2xl overflow-hidden bg-white/40">
            {savedSources.map((s) => {
              const active = selectedSourceId === s.id;
              const sizeMb = s.size ? (s.size / 1024 / 1024).toFixed(1) : "?";
              const when = s.uploaded_at ? new Date(s.uploaded_at).toLocaleString() : "";
              const sum = s.summary || {};
              const downloadSaved = async (id, name) => {
                const token = localStorage.getItem("access_token");
                const ws = localStorage.getItem("workspace_id");
                try {
                  const resp = await fetch(`${API}/backfill/uploads/${id}/download`, {
                    headers: { Authorization: `Bearer ${token}`, ...(ws ? { "X-Workspace": ws } : {}) },
                  });
                  if (!resp.ok) { alert(`Download failed: HTTP ${resp.status}`); return; }
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = name || `source-${id.slice(-6)}`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (e) { alert(`Download failed: ${e.message}`); }
              };
              const selectRow = () => {
                setSelectedSourceId(active ? null : s.id);
                setFile(null);
                setPreviewToken(null);
                setPreviewFileName(null);
                setResult(null);
              };
              return (
                <div
                  key={s.id}
                  onClick={selectRow}
                  className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${active ? "bg-emerald-50/80 ring-2 ring-emerald-400 ring-inset" : "hover:bg-white/60"}`}
                  data-testid={`saved-source-row-${s.id}`}
                >
                  <div
                    className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${active ? "bg-emerald-500 border-emerald-500 text-white shadow-md" : "border-stone-400 bg-white hover:border-emerald-500 hover:bg-emerald-50"}`}
                    title={active ? "Selected — click again to deselect" : "Click to select this source for Preview / Import"}
                    data-testid={`saved-source-select-${s.id}`}
                  >
                    {active ? <span className="text-sm leading-none font-bold">✓</span> : <span className="text-stone-400 text-xs">○</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate flex items-center gap-2" title={s.filename}>
                      {s.filename || "(unnamed)"}
                      {active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-semibold">SELECTED</span>}
                    </div>
                    <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>{sizeMb} MB</span>
                      {when && <span>{when}</span>}
                      {s.uploaded_by_name && <span>by {s.uploaded_by_name}</span>}
                      {typeof sum.files_processed === "number" && (
                        <span className="text-emerald-700">
                          {sum.files_processed} files · {sum.created_entries || 0} entries
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadSaved(s.id, s.filename); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-stone-200 hover:bg-white/80 text-stone-700"
                    title="Download the original file to your computer (optional)"
                    data-testid={`saved-source-download-${s.id}`}
                  >
                    Download
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSource(s.id); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                    title="Remove the raw file from the server (imported entries stay)"
                    data-testid={`saved-source-delete-${s.id}`}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
          {!selectedSourceId && savedSources.length > 0 && (
            <p className="mt-3 text-xs text-stone-500 italic">
              ⬆ Click a row above to select it, then use the Preview / Import buttons that appear.
            </p>
          )}
          {selectedSourceId && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => upload(true)}
                disabled={busy}
                className="btn-ghost border-2 border-[color:var(--gold)]/60 hover:bg-white/80 font-semibold"
                data-testid="backfill-saved-preview-btn"
              >
                {busy ? "…" : "▶ Preview from saved (dry run)"}
              </button>
              <button
                onClick={() => upload(false)}
                disabled={busy}
                className="btn-gold font-semibold"
                data-testid="backfill-saved-import-btn"
              >
                {busy ? "Processing…" : "⚡ Import from saved"}
              </button>
            </div>
          )}
        </GlassCard>
      )}

      {err && <div className="bg-red-50/70 text-red-700 rounded-2xl px-4 py-3 text-sm">{err}</div>}

      {(busy || progressLog.length > 0) && tab === "pdf" && (
        <GlassCard data-testid="backfill-progress-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-tiny text-stone-600">Progress</div>
              <div className="font-serif text-xl">{busy ? "Working…" : "Done"}</div>
            </div>
            <div className="text-xs text-stone-500 tabular-nums">{progressPct}%</div>
          </div>
          <div className="mt-3 h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--gold)] transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div
            className="mt-4 max-h-64 overflow-y-auto rounded-xl bg-stone-900/95 text-stone-100 px-4 py-3 font-mono text-[11px] leading-relaxed"
            data-testid="progress-log"
          >
            {progressLog.map((l, i) => {
              const msg = l.msg || "";
              const isMismatch = msg.includes("MISMATCH");
              const isOk = msg.includes("✓");
              const isWarn = msg.includes("⚠");
              const cls = isMismatch
                ? "text-red-400 font-semibold"
                : isWarn
                ? "text-amber-300"
                : isOk
                ? "text-emerald-300"
                : "";
              return (
                <div key={i} className="whitespace-pre-wrap">
                  <span className="text-stone-500 mr-2">[{l.t}]</span>
                  <span className={cls}>{msg}</span>
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        </GlassCard>
      )}

      {result && tab === "pdf" && (() => {
        const totalWarnings = (result.results || []).reduce((s, r) => s + (r.warnings?.length || 0), 0);
        const needsAck = result._isPreview && totalWarnings > 0;
        return (
        <GlassCard>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-serif text-2xl">
              {result._isPreview ? "Preview" : "Result"} — {result.files_processed} file(s)
              {totalWarnings > 0 && (
                <span className="ml-3 text-sm font-normal text-amber-700">
                  · {totalWarnings} warning{totalWarnings > 1 ? "s" : ""}
                </span>
              )}
            </h3>
            {result._isPreview && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60">
                  Dry run — nothing saved yet
                </span>
                {needsAck && (
                  <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={reviewedWarnings}
                      onChange={(e) => setReviewedWarnings(e.target.checked)}
                      data-testid="ack-warnings-checkbox"
                      className="rounded"
                    />
                    I&rsquo;ve reviewed the warnings
                  </label>
                )}
                <button
                  onClick={() => upload(false)}
                  disabled={busy || (needsAck && !reviewedWarnings)}
                  className="btn-gold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="backfill-confirm-btn"
                >
                  {busy ? "Importing…" : "Confirm & import"}
                </button>
              </div>
            )}
          </div>
          <div className="space-y-3 mt-4">
            {result.results.map((r, i) => (
              <div key={i} className="glass rounded-2xl p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-medium">{r.source}</div>
                  {r.error ? (
                    <span className="text-red-600 text-sm">Error: {r.error}</span>
                  ) : (
                    <span className="text-xs text-stone-500">Account: <b>{r.account_name}</b></span>
                  )}
                </div>
                {!r.error && (
                  <div className="grid grid-cols-3 gap-4 mt-3 text-center">
                    <div><div className="label-tiny">Accounts +</div><div className="font-serif text-2xl">{r.created_accounts}</div></div>
                    <div><div className="label-tiny">Entries +</div><div className="font-serif text-2xl">{r.created_entries}</div></div>
                    <div><div className="label-tiny">Duplicates skipped</div><div className="font-serif text-2xl text-amber-600">{r.duplicate_entries}</div></div>
                  </div>
                )}
                {!r.error && r.warnings && r.warnings.length > 0 && (
                  <details className="mt-3" data-testid={`warn-details-${i}`} open={result._isPreview}>
                    <summary className="cursor-pointer text-xs text-orange-700 hover:underline">
                      ⚠ {r.warnings.length} row{r.warnings.length > 1 ? "s" : ""} the parser wasn&rsquo;t sure about
                    </summary>
                    <div className="mt-2 space-y-1.5">
                      {r.warnings.map((w, j) => (
                        <div key={j} className="text-xs bg-orange-50/60 rounded-xl p-3 border border-orange-100">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="uppercase tracking-wide text-[10px] font-medium text-orange-700">
                              {w.reason.replace(/_/g, " ")}
                            </span>
                            <span className="text-stone-500">{w.date}</span>
                          </div>
                          <div className="text-stone-700 mt-0.5">{w.detail}</div>
                          <div className="text-stone-400 mt-1 font-mono truncate">{w.raw}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {!r.error && r.duplicates_detail && r.duplicates_detail.length > 0 && (
                  <details className="mt-3" data-testid={`dup-details-${i}`}>
                    <summary className="cursor-pointer text-xs text-amber-700 hover:underline">
                      Review {r.duplicates_detail.length} skipped duplicate{r.duplicates_detail.length > 1 ? "s" : ""}
                    </summary>
                    <div className="mt-2 space-y-2">
                      {r.duplicates_detail.map((d, j) => (
                        <div key={j} className="text-xs bg-amber-50/60 rounded-xl p-3 border border-amber-100">
                          <div className="grid md:grid-cols-2 gap-2">
                            <div>
                              <div className="label-tiny text-amber-700">This PDF wanted to add</div>
                              <div className="mt-1"><b>{(d.new.date || "").slice(0, 10)}</b> · {d.new.from} → {d.new.to}</div>
                              <div>Amount: <b>{Number(d.new.amount).toLocaleString()}</b></div>
                              {d.new.note && <div className="text-stone-600">Note: {d.new.note}</div>}
                            </div>
                            <div>
                              <div className="label-tiny text-stone-600">Existing entry matched</div>
                              <div className="mt-1">{(d.existing.created_at || "").slice(0, 10)}</div>
                              <div>Amount: <b>{Number(d.existing.amount).toLocaleString()}</b></div>
                              {d.existing.note && <div className="text-stone-600">Note: {d.existing.note}</div>}
                              <div className="text-stone-400 mt-0.5">from: {d.existing.created_by_name}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
          {result.tally && (
            <div className="mt-6">
              <h4 className="font-serif text-xl mb-2">Balance-sheet tally — {result.tally.source}</h4>
              {result.tally.error ? (
                <div className="text-red-600 text-sm">{result.tally.error}</div>
              ) : result.tally.all_tallied ? (
                <div className="bg-emerald-50/70 text-emerald-700 rounded-2xl px-4 py-3 text-sm">
                  ✅ All {result.tally.matches} accounts match the balance sheet.
                </div>
              ) : (
                <div className="bg-amber-50/70 rounded-2xl px-4 py-3 text-sm space-y-2">
                  <div><b>{result.tally.matches}</b> match · <b>{result.tally.mismatches.length}</b> mismatched · <b>{result.tally.unknown_accounts.length}</b> unknown</div>
                  {result.tally.mismatches.slice(0, 20).map((m, i) => (
                    <div key={i} className="text-xs text-amber-800">
                      <b>{m.name}</b>: expected {m.expected.toLocaleString()} vs actual {m.actual.toLocaleString()} (diff {m.diff.toLocaleString()})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {result.predicted_balance_sheet && (
            <PredictedBalanceSheet bs={result.predicted_balance_sheet} />
          )}
        </GlassCard>
        );
      })()}

      {/* Danger zone */}
      <GlassCard className="border-2 border-red-100">
        <h3 className="font-serif text-xl text-rose-700">Danger zone</h3>
        <p className="text-sm text-stone-600 mt-1">
          Wipe all Live accounts and entries. A JSON snapshot is auto-saved to
          <code className="mx-1 text-xs bg-white/80 px-1.5 py-0.5 rounded">/app/backups/</code>
          before deletion. Demo data is untouched.
        </p>
        <button
          onClick={async () => {
            const confirmation = prompt('Type CLEAR (all caps) to wipe all Live data:');
            if (confirmation !== "CLEAR") return;
            setBusy(true);
            setErr("");
            setResult(null);
            setClearedInfo(null);
            try {
              const { data } = await api.post("/admin/clear-live", { confirmation });
              setClearedInfo(data);
              window.dispatchEvent(new Event("workspace-changed"));
            } catch (e) {
              setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
              setBusy(false);
            }
          }}
          className="btn-ghost text-rose-600 mt-3 text-sm"
          data-testid="clear-live-btn"
        >
          Clear all live data (irreversible)
        </button>
      </GlassCard>

      {result && tab === "xlsx" && (
        <GlassCard>
          <h3 className="font-serif text-2xl">Result</h3>
          <div className="grid grid-cols-3 gap-4 mt-4 text-center">
            <div><div className="label-tiny">Created</div><div className="font-serif text-2xl">{result.created}</div></div>
            <div><div className="label-tiny">Updated</div><div className="font-serif text-2xl">{result.updated}</div></div>
            <div><div className="label-tiny">Errors</div><div className="font-serif text-2xl">{result.errors?.length || 0}</div></div>
          </div>
        </GlassCard>
      )}

      {inspectData && (
        <InspectModal
          data={inspectData}
          typeChoice={inspectTypeChoice}
          onTypeChange={(t) => { setInspectTypeChoice(t); inspectFile(t); }}
          onClose={() => setInspectData(null)}
        />
      )}
    </div>
  );
}

function InspectModal({ data, typeChoice, onTypeChange, onClose }) {
  const bs = data.balance_sheet_parsed;
  const ledger = data.ledger_parsed;
  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
      data-testid="inspect-modal"
    >
      <div
        className="glass rounded-3xl max-w-5xl w-full p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-tiny">Inspect · no DB writes</p>
            <h3 className="font-serif text-3xl">{data.filename}</h3>
            <p className="text-sm text-stone-600 mt-1">
              Detected as <b>{data.detected_type === "ledger" ? "Ledger" : "Balance Sheet"}</b> · {data.raw_text_total_lines} text lines
              {data.raw_text_truncated && <span className="text-stone-500"> (showing first 500)</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/60"
            data-testid="inspect-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-2 text-xs">
          <span className="label-tiny self-center">Re-parse as:</span>
          {["auto", "ledger", "balance_sheet"].map((t) => (
            <button
              key={t}
              onClick={() => onTypeChange(t)}
              className={`px-3 py-1 rounded-full ${typeChoice === t ? "bg-[color:var(--gold)] text-white" : "bg-white/70 text-stone-700 hover:bg-white"}`}
              data-testid={`inspect-force-${t}`}
            >
              {t === "auto" ? "Auto-detect" : t === "ledger" ? "Ledger" : "Balance sheet"}
            </button>
          ))}
        </div>

        {data.notes?.length > 0 && (
          <div className="bg-amber-50/70 border border-amber-200 rounded-2xl px-4 py-3 text-sm space-y-1">
            {data.notes.map((n, i) => (
              <p key={i} className="text-amber-900">{n}</p>
            ))}
          </div>
        )}

        {data.parse_error && (
          <div className="bg-red-50/70 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
            {data.parse_error}
          </div>
        )}

        {bs && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3 text-center">
              <StatBox label={`Rows (+ ${bs.positives_count} / − ${bs.negatives_count})`} value={bs.count} />
              <StatBox label="Sum (+)" value={bs.sum_positive.toLocaleString()} tone="green" />
              <StatBox label="Sum (−)" value={bs.sum_negative.toLocaleString()} tone="rose" />
              <StatBox label="Difference" value={bs.difference.toLocaleString()} tone={Math.abs(bs.difference) < 1 ? "green" : "rose"} />
            </div>
            <p className="text-xs text-stone-500">
              Every row below shows the exact raw PDF line and how it was parsed. If sums look wrong, scan the &ldquo;Raw source line&rdquo; column — a multi-column layout can cause pdfplumber to merge two accounts into one line.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <BSTable title={`Positives (${bs.positives_count}) · Σ = ${bs.sum_positive.toLocaleString()}`}
                       entries={bs.entries.filter((e) => e.amount > 0)} tone="green" />
              <BSTable title={`Negatives (${bs.negatives_count}) · Σ = ${bs.sum_negative.toLocaleString()}`}
                       entries={bs.entries.filter((e) => e.amount < 0)} tone="rose" />
            </div>
          </div>
        )}

        {ledger && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <StatBox label="Account name" value={ledger.account_name} />
              <StatBox label="Rows parsed" value={ledger.entries.length} />
              <StatBox label="Warnings" value={ledger.warnings.length} tone={ledger.warnings.length ? "rose" : "green"} />
            </div>
            <div className="max-h-80 overflow-y-auto rounded-2xl border border-white/60 bg-white/50">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white/90 backdrop-blur">
                  <tr className="text-left text-stone-500">
                    <th className="px-2 py-2 font-medium">Date</th>
                    <th className="px-2 py-2 font-medium">Counterparty</th>
                    <th className="px-2 py-2 font-medium">Note</th>
                    <th className="px-2 py-2 font-medium text-right">Change</th>
                    <th className="px-2 py-2 font-medium text-right">Balance (pre)</th>
                    <th className="px-2 py-2 font-medium">Src</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e, i) => (
                    <tr key={i} className="border-t border-white/60">
                      <td className="px-2 py-1 font-mono">{(e.date_iso || "").slice(0, 10)}</td>
                      <td className="px-2 py-1">{e.counterparty}</td>
                      <td className="px-2 py-1 text-stone-500 truncate max-w-xs">{e.note}</td>
                      <td className={`px-2 py-1 text-right tabular-nums ${e.change < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                        {e.change.toLocaleString()}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-stone-500">{e.balance?.toLocaleString?.() ?? ""}</td>
                      <td className="px-2 py-1 text-[10px] uppercase tracking-wide text-stone-400">
                        {e.auto_derived ? "auto" : "pdf"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <details className="rounded-2xl border border-white/60 bg-white/40">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium select-none">
            Raw text lines ({data.raw_text_lines.length}{data.raw_text_truncated ? " of " + data.raw_text_total_lines : ""})
          </summary>
          <pre className="text-[11px] font-mono px-4 py-3 max-h-96 overflow-auto whitespace-pre-wrap break-words leading-snug">
            {data.raw_text_lines.map((l, i) => `${String(i + 1).padStart(4, " ")}  ${l}`).join("\n")}
          </pre>
        </details>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone = "neutral" }) {
  const toneCls = tone === "green"
    ? "text-emerald-700"
    : tone === "rose"
    ? "text-rose-600"
    : "text-stone-800";
  return (
    <div className="glass rounded-2xl px-3 py-2">
      <div className="label-tiny">{label}</div>
      <div className={`font-serif text-xl truncate ${toneCls}`}>{value}</div>
    </div>
  );
}

function PredictedBalanceSheet({ bs }) {
  const diffTone = Math.abs(bs.difference) <= 10 ? "green" : "rose";
  return (
    <div className="mt-6 space-y-3" data-testid="predicted-balance-sheet">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h4 className="font-serif text-2xl">Predicted Balance Sheet</h4>
        <span className="text-xs text-stone-500">This is what the app&apos;s Balance Sheet page will show after Import.</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <StatBox label={`Positives (${bs.positives.length})`} value={fmtNum(bs.sum_positive)} tone="green" />
        <StatBox label={`Negatives (${bs.negatives.length})`} value={fmtNum(bs.sum_negative)} tone="rose" />
        <StatBox label="Difference" value={fmtNum(bs.difference)} tone={diffTone} />
        <StatBox label="Accounts total" value={bs.accounts_total} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <PredictedBSTable title={`Positive balances · Σ = ${fmtNum(bs.sum_positive)}`}
                          rows={bs.positives} tone="green" />
        <PredictedBSTable title={`Negative balances · Σ = ${fmtNum(bs.sum_negative)}`}
                          rows={bs.negatives} tone="rose" />
      </div>
      {bs.zeros_or_near_zero.length > 0 && (
        <details className="glass rounded-2xl">
          <summary className="cursor-pointer px-4 py-2 text-sm font-medium">
            Zero / near-zero accounts ({bs.zeros_or_near_zero.length})
          </summary>
          <div className="px-4 pb-3 grid md:grid-cols-3 gap-x-6 gap-y-1 text-xs">
            {bs.zeros_or_near_zero.map((z, i) => (
              <div key={i} className="flex justify-between gap-2 border-b border-white/50 py-1">
                <span className="font-mono truncate" title={z.name}>{z.name}</span>
                <span className="tabular-nums text-stone-500">{fmtNum(z.balance)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function PredictedBSTable({ title, rows, tone }) {
  const amtCls = tone === "rose" ? "text-rose-600" : "text-emerald-700";
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="px-3 py-2 border-b border-white/50 text-xs font-medium text-stone-600 bg-white/40">
        {title}
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white/90 backdrop-blur text-stone-500">
            <tr className="text-left">
              <th className="px-3 py-1.5 font-medium">Account</th>
              <th className="px-3 py-1.5 font-medium text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-white/60 hover:bg-white/40">
                <td className="px-3 py-1 font-mono truncate max-w-[16rem]" title={r.name}>{r.name}</td>
                <td className={`px-3 py-1 text-right tabular-nums ${amtCls}`}>{fmtNum(r.balance)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={2} className="px-3 py-4 text-center text-stone-500 italic">None</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BSTable({ title, entries, tone }) {
  const amtCls = tone === "rose" ? "text-rose-600" : "text-emerald-700";
  // Running sum for spot-checking which row throws the total off
  let running = 0;
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="px-3 py-2 border-b border-white/50 text-xs font-medium text-stone-600 bg-white/40">
        {title}
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white/90 backdrop-blur text-stone-500">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-medium">Account</th>
              <th className="px-2 py-1.5 font-medium text-right">Amount</th>
              <th className="px-2 py-1.5 font-medium text-right">Running Σ</th>
              <th className="px-2 py-1.5 font-medium">Raw source line</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              running += e.amount;
              return (
                <tr key={i} className="border-t border-white/60 hover:bg-white/40">
                  <td className="px-2 py-1 font-mono truncate max-w-[10rem]" title={e.name}>{e.name}</td>
                  <td className={`px-2 py-1 text-right tabular-nums ${amtCls}`}>{e.amount.toLocaleString()}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-stone-500">{running.toLocaleString()}</td>
                  <td className="px-2 py-1 font-mono text-[10px] text-stone-500 truncate max-w-[16rem]" title={e.raw_line}>
                    {e.raw_line}
                  </td>
                </tr>
              );
            })}
            {entries.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-stone-500 italic">No {tone === "rose" ? "negative" : "positive"} rows.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
